import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import { useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Image as RbImage,
  InputGroup,
  Placeholder,
  Row,
  Spinner
} from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimesCircle,
  faFileImage,
  faCloudUploadAlt
} from '@fortawesome/free-solid-svg-icons';

import AuthContext from '../../../Context/authContext';
import { getOwnerSpots, updateSpot } from "../../../Services/api";
import { spotCategoryMapping } from "../constants";
import PlaceholderImg from "../../../Assets/Images/download.svg";
import Notification, { useNotification } from 'src/Components/Notification';
import MenuQRCode from "src/Components/QRCode";
import COUNTRY_STATES_MAPPING from 'src/Pages/account/countries';

import "./style.scss";


const getFieldValue = (name, updatedDetails, details) =>
  updatedDetails[name] ?? details[name] ?? "";

const PhotoManager = ({
  existingImages,
  newImages,
  onRemoveExisting,
  onAddFiles,
  onRemoveNew
}) => {
  const fileInputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length) onAddFiles(files);
  };

  return (
    <Card className="edit-spot-card">
      <Card.Body>
        <Card.Title className="edit-spot-card-title">Photos</Card.Title>
        <p className="edit-spot-card-hint">
          Add a few clear photos of the spot. You can drag and drop, or
          browse from your device.
        </p>
        <div className="photo-grid">
          <div
            className={`photo-tile photo-tile--upload${
              isDragging ? " is-dragging" : ""
            }`}
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                fileInputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" />
            <span className="photo-tile-label">Add photos</span>
            <Form.Control
              multiple
              type="file"
              accept="image/*"
              ref={fileInputRef}
              className="visually-hidden"
              aria-label="Upload spot images"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = null;
              }}
            />
          </div>

          {existingImages.map((image, i) => (
            <div className="photo-tile" key={`existing-${image.id ?? image.url ?? i}`}>
              <RbImage
                src={image.url}
                rounded
                onError={(e) => {
                  e.target.src = PlaceholderImg;
                }}
              />
              <button
                type="button"
                className="photo-remove-btn"
                aria-label="Remove photo"
                onClick={() => onRemoveExisting(i)}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
          ))}

          {newImages.map((img, i) => (
            <div className="photo-tile photo-tile--new" key={img.previewUrl}>
              <RbImage src={img.previewUrl} rounded />
              <Badge bg="success" className="photo-new-badge">
                New
              </Badge>
              <button
                type="button"
                className="photo-remove-btn"
                aria-label="Remove new photo"
                onClick={() => onRemoveNew(i)}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
          ))}

          {existingImages.length === 0 && newImages.length === 0 && (
            <div className="photo-empty-hint">
              <FontAwesomeIcon icon={faFileImage} size="2x" />
              <span>No photos yet</span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const FormSkeleton = () => (
  <div>
    {[0, 1, 2].map((i) => (
      <Card className="edit-spot-card" key={i}>
        <Card.Body>
          <Placeholder as={Card.Title} animation="glow">
            <Placeholder xs={4} />
          </Placeholder>
          <Placeholder as="div" animation="glow" className="d-flex gap-3 mt-3">
            <Placeholder xs={5} style={{ height: 15 }} />
            <Placeholder xs={5} style={{ height: 15 }} />
          </Placeholder>
        </Card.Body>
      </Card>
    ))}
  </div>
);

const EditSpot = () => {
  const [details, setDetails] = useState({});
  const [updatedDetails, setUpdatedDetails] = useState({});
  const [existingImages, setExistingImages] = useState([]);
  const [removedExistingImages, setRemovedExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [updated, setUpdated] = useState(false);
  const { user } = useContext(AuthContext);
  let { id: spotId } = useParams();
  const { notifySuccess, notifyError, onClose, notification } = useNotification();

  const getSpot = useCallback(async () => {
    setIsFetching(true);
    const resp = await getOwnerSpots(user.id, spotId);
    if (resp.status === 200) {
      setDetails(resp.data);
      setExistingImages(resp.data.images || []);
    } else {
      notifyError({ message: resp.message });
    }
    setIsFetching(false);
  }, [user, spotId, notifyError]);

  useEffect(() => {
    if ((user && Object.keys(user).length)) {
      getSpot();
    }
  }, [getSpot, user]);

  useEffect(() => {
    if (updated) {
      getSpot();
      setUpdated(false);
    }
  }, [updated])

  useEffect(() => {
    return () => {
      newImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [newImages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedDetails((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleAddFiles = (files) => {
    setNewImages((prev) => [
      ...prev,
      ...files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }))
    ]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => {
      setRemovedExistingImages((removed) => [...removed, prev[index]]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const isDirty =
    Object.keys(updatedDetails).length > 0 ||
    newImages.length > 0 ||
    removedExistingImages.length > 0;

  const handleSpotUpdate = async () => {
    setIsLoading(true);
    const spotDataToUpdate = new FormData();

    Object.entries(updatedDetails).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => spotDataToUpdate.append(key, item));
      } else {
        spotDataToUpdate.append(key, value);
      }
    });

    newImages.forEach(({ file }) => spotDataToUpdate.append("images", file));
    spotDataToUpdate.append(
      "existingImages",
      existingImages.map((image) => image.id).join(",")
    );

    const resp = await updateSpot({ id: spotId, data: spotDataToUpdate });
    if (resp.status !== 200) {
      notifyError({ message: resp.response?.data?.detail || "Update failed" });
    } else {
      setUpdated(true);
      setUpdatedDetails({});
      setNewImages([]);
      setRemovedExistingImages([]);
      notifySuccess({ message: "Account details updated successfully!" });
    }
    setIsLoading(false);
  };

  const categoryValue = useMemo(
    () => getFieldValue("category", updatedDetails, details),
    [updatedDetails, details]
  );

  const states = useMemo(() => {
    if (!details?.country) { return [] }
    return COUNTRY_STATES_MAPPING[details.country]
  }, [details.country])

  return (
    <Container className="pt-4 edit-spot-page">
      <Notification
        id="edit-spot-notif"
        onClose={() => {
          onClose();
          // notification.type === "success" && setUpdated(false);
        }}
        {...notification}
      />
      <div className="edit-spot-header">
        <h5 className="mb-0">Account details</h5>
        {details.category && (
          <Badge bg="light" text="dark" className="edit-spot-category-badge">
            {Object.keys(spotCategoryMapping).find(
              (key) => spotCategoryMapping[key] === details.category
            ) || details.category}
          </Badge>
        )}
      </div>

      {isFetching ? (
        <FormSkeleton />
      ) : (
        <Row className="gy-4">
          <Col xs={12} lg={8}>
            <Form>
              <Card className="edit-spot-card">
                <Card.Body>
                  <Card.Title className="edit-spot-card-title">
                    Basic info
                  </Card.Title>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group controlId="formName">
                        <Form.Label>Spot name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={getFieldValue("name", updatedDetails, details)}
                          onChange={handleInputChange}
                          placeholder="Name of spot"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="formBasicEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={getFieldValue("email", updatedDetails, details)}
                          onChange={handleInputChange}
                          placeholder="Email"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="formCategory">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                          name="category"
                          value={categoryValue}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled>
                            Select a category
                          </option>
                          {Object.entries(spotCategoryMapping).map(
                            ([label, value]) => (
                              <option key={label} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="formPhoneNo">
                        <Form.Label>Phone number</Form.Label>
                        <Form.Control
                          type="tel"
                          inputMode="tel"
                          name="phone_no"
                          value={getFieldValue("phone_no", updatedDetails, details)}
                          onChange={handleInputChange}
                          placeholder="Phone number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="edit-spot-card">
                <Card.Body>
                  <Card.Title className="edit-spot-card-title">
                    Location &amp; hours
                  </Card.Title>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group controlId="formStreetNo">
                        <Form.Label>Spot address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={getFieldValue("address", updatedDetails, details)}
                          onChange={handleInputChange}
                          placeholder="Spot address"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                      <Form.Group controlId="formCity">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={getFieldValue(
                            "city",
                            updatedDetails,
                            details
                          )}
                          onChange={handleInputChange}
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                      <Form.Group controlId="formState">
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="state"
                          value={getFieldValue("state", updatedDetails, details)}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled>Select State</option>
                          {!!states && states.map((country_state) => (<option key={country_state} value={country_state}>{country_state}</option>))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                      <Form.Group controlId="formCountry">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={getFieldValue(
                            "country",
                            updatedDetails,
                            details
                          )}
                          placeholder="Country"
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={4}>
                      <Form.Group controlId="formOpens">
                        <Form.Label>Opens</Form.Label>
                        <Form.Control
                          type="text"
                          name="time_of_opening"
                          value={getFieldValue(
                            "time_of_opening",
                            updatedDetails,
                            details
                          )}
                          onChange={handleInputChange}
                          placeholder="e.g. 9:00 AM"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={4}>
                      <Form.Group controlId="formCloses">
                        <Form.Label>Closes</Form.Label>
                        <Form.Control
                          type="text"
                          name="time_of_closing"
                          value={getFieldValue(
                            "time_of_closing",
                            updatedDetails,
                            details
                          )}
                          onChange={handleInputChange}
                          placeholder="e.g. 9:00 PM"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                      <Form.Group controlId="formTaxRate">
                        <Form.Label>Tax rate</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            name="tax_rate"
                            value={getFieldValue(
                              "tax_rate",
                              updatedDetails,
                              details
                            )}
                            onChange={handleInputChange}
                            placeholder="0.0"
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="edit-spot-card">
                <Card.Body>
                  <Card.Title className="edit-spot-card-title">
                    Description
                  </Card.Title>
                  <Form.Group controlId="formDesc">
                    <Form.Control
                      name="description"
                      as="textarea"
                      rows={4}
                      value={getFieldValue(
                        "description",
                        updatedDetails,
                        details
                      )}
                      onChange={handleInputChange}
                      placeholder="Description about spot"
                    />
                  </Form.Group>
                </Card.Body>
              </Card>

              <PhotoManager
                existingImages={existingImages}
                newImages={newImages}
                onRemoveExisting={handleRemoveExistingImage}
                onAddFiles={handleAddFiles}
                onRemoveNew={handleRemoveNewImage}
              />
            </Form>

            <div className="edit-spot-actionbar">
              <Button
                className="business w-50"
                type="submit"
                onClick={handleSpotUpdate}
                disabled={isLoading || !isDirty}
              >
                {isLoading && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                )}
                Update
              </Button>
            </div>
          </Col>

          <Col xs={12} lg={4} className="mb-4">
            <div className="edit-spot-qr-sticky">
              <MenuQRCode spotName={details.name} url={details.menu_url} />
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default EditSpot;
